/**
 * Запекание поля в спрайт-лист.
 *
 * Вход — поле времени прибытия. Выход — одна картинка из N кадров подряд
 * по горизонтали и её URL. Дальше проигрывание не считает ничего: браузер
 * двигает окно по готовым пикселям.
 *
 * ── Два канала в одном листе ──────────────────────────────────────────
 * Ободок, бегущий за фронтом, — это `порог − w < T ≤ порог`. Такая полоса
 * НЕ монотонна и одной маской быть не может. Но она — разность двух
 * монотонных, и обе кладутся в одну картинку:
 *
 *   ALPHA → ведущий фронт   (больший)  — им раскрывается подтёк
 *   RGB   → отставший фронт (меньший)  — им раскрывается содержимое
 *
 * Слой содержимого берёт `mask-mode: luminance`, то есть яркость, умноженную
 * на альфу. Поскольку отставший фронт ЦЕЛИКОМ лежит внутри ведущего,
 * произведение равно самому отставшему — ровно то, что нужно. Слой подтёка
 * берёт альфу как есть.
 *
 * Побочная выгода этой раскладки: canvas хранит цвет домноженным на альфу,
 * и RGB там, где альфа нулевая, теряется при кодировании. У нас в этих
 * местах RGB и так ноль — вложенность гарантирует, что данные не пропадут.
 * Обратная раскладка (содержимое в альфу) выглядела бы столь же логично
 * и молча портила бы кромку.
 *
 * Если клиент не знает `mask-mode`, оба слоя читают альфу: ободок исчезает,
 * раскрытие остаётся правильным. Деградация без единой проверки версии.
 */

/** Готовый лист. `release` обязателен: Blob-URL живёт, пока его не отпустят. */
export interface Sheet {
  url: string
  frames: number
  width: number
  height: number
  release: () => void
}

export interface BakeOptions {
  frames: number
  /** Мягкость края в единицах поля. 0 — трафарет, 0.08 — размытая тушь. */
  feather: number
  /** Отставание внутреннего фронта. 0 отключает ободок. */
  lag: number
}

/**
 * Собрать лист.
 *
 * Стоимость здесь — не в поле, а в этом цикле: N×w×h операций «сравнить
 * и записать». Для 14 кадров при 120×220 это 370 тысяч записей, 1.4–1.8 мс,
 * и они одинаковы для любого механизма. Поэтому механизмы выбираются
 * по внешнему виду, а не по цене.
 */
export function bake(
  field: Float32Array,
  w: number,
  h: number,
  levels: Float32Array,
  opts: BakeOptions,
): Sheet {
  const { frames, feather, lag } = opts
  const canvas = document.createElement('canvas')
  canvas.width = w * frames
  canvas.height = h

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('нет 2d-контекста: запечь лист нечем')

  const img = ctx.createImageData(w * frames, h)
  const data = img.data
  const invFeather = feather > 0 ? 1 / feather : 0

  for (let k = 0; k < frames; k++) {
    const lead = levels[k]!
    const trail = lead - lag
    const xOffset = k * w

    for (let y = 0; y < h; y++) {
      const row = y * w * frames
      for (let x = 0; x < w; x++) {
        const t = field[y * w + x]!

        // Мягкий край: одно умножение, и по кадрам функция не убывает —
        // значит вложенность цела. Именно это отличает размыв туши
        // от вырезанного трафарета.
        const a = invFeather === 0 ? (t <= lead ? 255 : 0) : clamp255((lead - t) * invFeather)
        const b = invFeather === 0 ? (t <= trail ? 255 : 0) : clamp255((trail - t) * invFeather)

        const p = (row + xOffset + x) * 4
        data[p] = b
        data[p + 1] = b
        data[p + 2] = b
        data[p + 3] = a
      }
    }
  }

  ctx.putImageData(img, 0, 0)

  let url = ''
  let released = false
  // toDataURL, а не toBlob: последний асинхронен, а лист нужен в том же
  // кадре, в котором его попросили. Разница по времени на картинке
  // такого размера — доли миллисекунды.
  url = canvas.toDataURL('image/png')

  return {
    url,
    frames,
    width: w,
    height: h,
    release: () => {
      if (released) return
      released = true
      canvas.width = 0
      canvas.height = 0
    },
  }
}

const clamp255 = (v: number): number => (v <= 0 ? 0 : v >= 1 ? 255 : (v * 255) | 0)
